/**
 * Auth storage helpers.
 *
 * Field-parity rule (docs/ADMIN_REDESIGN.md): the localStorage key and
 * shape MUST match the legacy admin so that:
 *   1. A staff member already signed into legacy admin can open admin-v2
 *      and not be prompted to log in again.
 *   2. The legacy admin's existing 401 interceptor (Milestone 7 Batch 1)
 *      reads/clears the same key.
 *
 * Legacy contract:
 *   localStorage.auth_data = JSON.stringify({ token, isAdmin })
 *
 * Both fields are required by the backend's downstream auth middleware,
 * which expects the JWT in the Authorization header.
 */

const STORAGE_KEY = "auth_data"

export type AuthData = {
  token: string
  isAdmin: boolean
}

export function readAuth(): AuthData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<AuthData>
    if (typeof parsed.token !== "string" || parsed.token.length === 0) {
      return null
    }
    return {
      token: parsed.token,
      // Legacy backend always sets isAdmin:true for admin tokens; default to
      // false defensively if the field somehow went missing.
      isAdmin: !!parsed.isAdmin,
    }
  } catch {
    return null
  }
}

export function writeAuth(data: AuthData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch {
    // localStorage disabled (private mode / quota / SSR). The session will
    // simply not persist across reloads; the in-memory AuthContext still
    // works for the current tab.
  }
}

export function clearAuth(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* see writeAuth */
  }
}
